import React, {useContext, useEffect, useCallback} from 'react';
import Section from './UI/Section';
import Button from './UI/Button';
import LoadingSpinner from './UI/LoadingSpinner';
import {Context} from '../context';
import Cards from './Cards';
import {useNavigate} from 'react-router-dom';
import {clone} from 'ramda';

function Checkout() {
  const {
    token,
    cart,
    clearCartPasses,
    account,
    selectedCardID,
    localeText,
    purchase,
    setPurchase,
    oneTimePaymentToken,
    setCheckout,
    setSnackBarMessage,
    oneTimePaymentPurchase,
    oneTimePaymentAddBalance,
    existingPaymentMethodPurchase,
    existingPaymentMethodAddBalance,
    paymentProcessing,
    showError,
    showCartIsEmpty,
    EMPTY_CART_OBJ
  } = useContext(Context);

  const navigate = useNavigate();

  useEffect(() => {
    return () => {
      setPurchase({purchaseConfirmationPage: false, cart: EMPTY_CART_OBJ});
      setCheckout(false);
    };
  }, [EMPTY_CART_OBJ, setCheckout, setPurchase]);

  const purchaseStoredValueAndPasses = useCallback(
    async ({
      cart,
      purchaseStoredValueAPI,
      purchaseStoredValueParams,
      purchasePassesAPI,
      purchasePassesParams
    }) => {
      const cartPurchase = clone(EMPTY_CART_OBJ);

      // 1. handle stored value purchase
      if (cart?.storedValue) {
        cartPurchase.storedValue = cart.storedValue;
        const purchaseStoredValueReturn = await purchaseStoredValueAPI(
          purchaseStoredValueParams
        );

        if (purchaseStoredValueReturn?.errors) {
          cartPurchase.storedValue = 0;
          showError(purchaseStoredValueReturn.errors);
          return;
        }

        setSnackBarMessage(
          `$${cart?.storedValue / 100} ${localeText(
            'rw_loadfunds_text_added_successfully_to_transit_account',
            'added successfully to Transit Account'
          )}`
        );
      }

      // 2. handle passes purchase
      if (cart?.passes.length) {
        cartPurchase.passes = cart.passes;
        const purchasePassesReturn = await purchasePassesAPI(
          purchasePassesParams
        );

        if (purchasePassesReturn?.errors) {
          cartPurchase.passes = [];
          showError(purchasePassesReturn.errors);
          return;
        }
      }

      clearCartPasses();
      setSnackBarMessage(
        localeText('rw_msg_purchase_successful', 'Purchase Successful')
      );

      setPurchase({purchaseConfirmationPage: true, cart: cartPurchase});
      return true;
    },
    [localeText, setSnackBarMessage, showError, clearCartPasses, setPurchase]
  );

  const completePurchase = async event => {
    event.preventDefault();

    if (cart?.passes?.length === 0 && cart?.storedValue === 0) {
      alert('cart empty');
      return;
    }

    if (!selectedCardID) {
      alert('no card selected');
      return;
    }

    if (oneTimePaymentToken && oneTimePaymentToken?.token === selectedCardID) {
      return await purchaseStoredValueAndPasses({
        cart,
        purchaseStoredValueAPI: oneTimePaymentAddBalance,
        purchaseStoredValueParams: {
          oneTimePaymentToken: oneTimePaymentToken.token,
          transitAccountId: account.transitAccounts[0].id,
          amount: cart?.storedValue
        },
        purchasePassesAPI: oneTimePaymentPurchase,
        purchasePassesParams: {
          oneTimePaymentToken: oneTimePaymentToken.token,
          cartPasses: cart?.passes
        }
      });
    }

    // existing Payment Method
    const paymentMethod = account?.paymentMethods?.find(
      ({id}) => id === selectedCardID
    );
    if (!paymentMethod) {
      alert('no payment method selected');
      return;
    }
    if (paymentMethod.processor === 'STORED_VALUE') {
      alert(`invalid payment method selected ${paymentMethod.processor}`);
      return;
    }

    return await purchaseStoredValueAndPasses({
      cart,
      purchaseStoredValueAPI: existingPaymentMethodAddBalance,
      purchaseStoredValueParams: {
        paymentMethod,
        transitAccount: account.transitAccounts[0],
        amount: cart?.storedValue
      },
      purchasePassesAPI: existingPaymentMethodPurchase,
      purchasePassesParams: {
        paymentMethod,
        cartPasses: cart?.passes
      }
    });
  };

  if (token && !account) {
    return <div>{localeText('rw_msg_loading', 'Loading ...')}</div>;
  }
  if (!account) {
    return (
      <div>{localeText('rw_msg_not_logged_in', 'You are not logged in.')}</div>
    );
  }
  if (purchase.purchaseConfirmationPage) {
    return (
      <Section heading={localeText('rw_header_checkout', 'Checkout')}>
        <h2>
          {localeText('rw_msg_purchase_successful', 'Purchase Successful')}
        </h2>
        <Button buttonId='return-to-buy' onClick={() => navigate('/buy')}>
          {localeText(
            'rw_button_go_back_to_purchase_passes',
            'Continue back to Purchase Passes'
          )}
        </Button>
      </Section>
    );
  }
  if (showCartIsEmpty) {
    return (
      <div>
        {localeText('rw_msg_empty_cart', 'You have no items in your cart.')}
      </div>
    );
  }

  return (
    <Section heading={localeText('rw_header_checkout', 'Checkout')}>
      <Cards />
      <div
        className={`p-3 my-4 rounded-md bg-orange-100 ${
          selectedCardID === 'addCard' ? 'hidden' : 'inline-block'
        }`}
      >
        {localeText(
          'rw_msg_card_will_be_charged',
          'Your card will be charged $'
        )}
        {(
          cart?.passes
            .map(item => item.price * item.quantity)
            .reduce((acc, row) => acc + row, 0) / 100
        ).toFixed(2)}
      </div>
      <div className={`${selectedCardID === 'addCard' && 'hidden'}`}>
        {account.paymentMethods && (
          <Button
            className='w-full'
            buttonId='complete-purchase'
            onClick={completePurchase}
            loading={paymentProcessing}
          >
            {localeText('rw_button_complete_purchase', 'Complete Purchase')}
          </Button>
        )}
      </div>

      {paymentProcessing && (
        <div className='flex flex-col items-center'>
          <LoadingSpinner className='mt-8' />
          <p className='text-xl'>
            {localeText('rw_msg_processing_payment', 'Processing Payment')}
            ...
          </p>
        </div>
      )}
    </Section>
  );
}

export default Checkout;
